import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

// Define types
interface MonthStatus {
  status: 'pending' | 'due' | 'completed' | 'issue' | null;
  value: number | null;
}

interface ActionItem {
  name: string;
  monthlyStatus: MonthStatus[];
}

interface ActionCategory {
  type: string;
  items: ActionItem[];
}

@Component({
  selector: 'app-action-list',
  templateUrl: './action-list.component.html',
  styleUrls: ['./action-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule, // Ensure CommonModule is imported
    // other modules
  ]
})
export class ActionListComponent implements OnInit {
  // List of months
  months: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  // Sample data
  managementPlan: ActionCategory[] = [
    {
      type: 'Regulatory Compliance',
      items: [
        {
          name: 'Environmental Compliance Assessment',
          monthlyStatus: [
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'pending', value: 1 }
          ]
        },
        {
          name: 'Occupational Safety Regulations Review',
          monthlyStatus: [
            { status: null, value: null },
            { status: 'completed', value: 2 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 2 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'issue', value: 2 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 2 },
            { status: null, value: null }
          ]
        }
      ]
    },
    {
      type: 'Chemical Management',
      items: [
        {
          name: 'Hazardous Chemicals Inventory',
          monthlyStatus: [
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'due', value: 1 },
            { status: null, value: null }
          ]
        },
        {
          name: 'SDS Updates',
          monthlyStatus: [
            { status: 'completed', value: 3 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 3 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 3 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'issue', value: 3 },
            { status: null, value: null },
            { status: 'pending', value: 3 }
          ]
        }
      ]
    },
    {
      type: 'Internal Audit',
      items: [
        {
          name: 'ISO 14001 Internal Audit',
          monthlyStatus: [
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 2 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 2 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 2 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'pending', value: 2 }
          ]
        },
        {
          name: 'ISO 45001 Internal Audit',
          monthlyStatus: [
            { status: null, value: null },
            { status: 'issue', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'due', value: 1 },
            { status: null, value: null }
          ]
        }
      ]
    },
    {
      type: 'External Audit',
      items: [
        {
          name: 'Certification Renewal',
          monthlyStatus: [
            { status: null, value: null },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: null, value: null },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null }
          ]
        },
        {
          name: 'Customer Audit',
          monthlyStatus: [
            { status: null, value: null },
            { status: 'completed', value: 2 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'issue', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'completed', value: 1 },
            { status: null, value: null },
            { status: null, value: null },
            { status: null, value: null },
            { status: 'pending', value: 1 }
          ]
        }
      ]
    }
  ];

  constructor() { }

  ngOnInit(): void {
  }

  // Return CSS class name based on status
  getStatusClass(status: MonthStatus): string {
    if (!status || status.status === null) return '';

    switch (status.status) {
      case 'pending': return 'status-pending';
      case 'due': return 'status-due';
      case 'completed': return 'status-completed';
      case 'issue': return 'status-issue';
      default: return '';
    }
  }

  // Status tooltip text
  getStatusTooltip(status: MonthStatus): string {
    if (!status || status.status === null) return '';

    switch (status.status) {
      case 'pending': return 'Scheduled but not yet due';
      case 'due': return 'Due but not completed';
      case 'completed': return 'Due and completed';
      case 'issue': return 'Due but issues found';
      default: return '';
    }
  }
}
